// React imports
import React, { useEffect, useState } from "react";

// External library imports
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { StandardTablePagination } from "components/Pagination/StandardTablePagination";
import { PAGINATION_CONFIG } from "utils/paginationConfig";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";

// Local imports
import PageTitle from "../../../components/Page Component/PageTitle";
import { RootState } from "../../../store/Store";
import {
  FetchDataFailureAction,
  FetchDataSuccessAction,
  deleteData,
  fetchData,
  postData,
} from "../../../store/actions";
import {
  ToastVariant,
  useCustomToast,
} from "../../../components/Methods/SnackBar";
import { deleteKRAUrl, kraTemplatesUrl } from "../../../Service/Apis";
import { formatDate, getPeriodShortLabel } from "../utils";
import { KRA, KRASection, KPIQuestion } from "./Types/KRATypes";

const AllKRATemplates: React.FC = () => {
  // useState declarations
  const [kras, setKRAs] = useState<KRA[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>("");
  const [designationFilter, setDesignationFilter] = useState<string>("all");
  const [viewDialogOpen, setViewDialogOpen] = useState<boolean>(false);
  const [selectedKRA, setSelectedKRA] = useState<KRA | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [kraToDelete, setKRAToDelete] = useState<KRA | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);
  
  // Sorting state
  const [sortField, setSortField] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  
  // Stats (constant across all pages, calculated from full dataset on backend)
  const [stats, setStats] = useState({
    total_kras: 0,
    active_kras: 0,
    draft_kras: 0,
    total_assignments: 0,
  });
  const [filterOptions, setFilterOptions] = useState<{
    designations: string[];
  }>({
    designations: []
  });

  // const variables (hooks, Redux, computed constants)
  const navigate = useNavigate();
  const showToast = useCustomToast();
  const dispatch: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();

  // Debounce search query - update debouncedSearchQuery after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // Wait 500ms after user stops typing

    return () => {
      clearTimeout(timer); // Cleanup timer on unmount or when searchQuery changes
    };
  }, [searchQuery]);

  useEffect(() => {
    fetchKRAs();
  }, [currentPage, pageSize, debouncedSearchQuery, designationFilter, sortField, sortOrder]);

  // Reset to page 1 when filters or sorting change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [debouncedSearchQuery, designationFilter, sortField, sortOrder]);

  const fetchKRAs = async () => {
    setLoading(true);
    try {
      // Build URL with pagination and filter parameters
      let url = `${kraTemplatesUrl}?page=${currentPage}&page_size=${pageSize}`;
      
      // Add filter parameters
      if (designationFilter !== "all") {
        url += `&designation=${encodeURIComponent(designationFilter)}`;
      }
      if (debouncedSearchQuery.trim()) {
        url += `&search=${encodeURIComponent(debouncedSearchQuery.trim())}`;
      }
      
      // Add sorting parameters
      if (sortField) {
        url += `&ordering=${sortOrder === "desc" ? "-" : ""}${sortField}`;
      }
      
      const response = await dispatch(fetchData(url));
      
      // Handle paginated response
      if (response && response.results) {
        setKRAs(response.results);
        setTotalPages(response.total_pages || 1);
        setTotalCount(response.count || 0);
        
        // Store stats from backend (constant across all pages)
        if (response.stats) {
          setStats(response.stats);
        }
        
        // Store filter options from backend (constant across all pages)
        if (response.filter_options) {
          setFilterOptions(response.filter_options);
        }
      } else if (response && Array.isArray(response)) {
        // Fallback for non-paginated response (shouldn't happen)
        setKRAs(response);
        setTotalPages(1);
        setTotalCount(response.length);
      } else {
        setKRAs([]);
        setTotalPages(1);
        setTotalCount(0);
      }
      
      
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = axiosError.response?.data?.error || axiosError.message || "Failed to load KRAs";
      showToast(errorMessage, "error", true);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle order if same field
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // Set new field with ascending order
      setSortField(field);
      setSortOrder("asc");
    }
  };


  const handleViewKRA = (kra: KRA) => {
    setSelectedKRA(kra);
    setViewDialogOpen(true);
  };

  const handleAssignKRA = (kra: KRA) => {
    navigate(`/pms/kra/assign/${kra.id}`, {
      state: {
        designationName: kra.designation,
        formData: {
          form_name: kra.form_name,
          sections: kra.sections,
        },
        reviewPeriod: kra.review_period || "quarterly",
      },
    });
  };

  const handleEditKRA = (kra: KRA) => {
    navigate(`/pms/kra/edit/${kra.id}`, {
      state: {
        designationName: kra.designation,
        designationId: kra.designation_id,
        templateData: {
          id: kra.id,
          form_name: kra.form_name,
          sections: kra.sections,
          is_draft: kra.is_draft,
        },
      },
    });
  };

  const handleDeleteClick = (kra: KRA) => {
    setKRAToDelete(kra);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!kraToDelete) return;

    setDeleting(true);
    try {
      await dispatch(deleteData(deleteKRAUrl(kraToDelete.id)));
      
      // Remove from local state
      setKRAs((prev) => prev.filter((kra) => kra.id !== kraToDelete.id));
      
      showToast("KRA template deleted successfully!", "success", true);
      setDeleteDialogOpen(false);
      setKRAToDelete(null);
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string; detail?: string } }; message?: string };
      const errorMessage = axiosError.response?.data?.error || axiosError.response?.data?.detail || axiosError.message || "Failed to delete KRA template. Please try again.";
      showToast(errorMessage, "error", true);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setKRAToDelete(null);
  };

  // useEffect hooks
  useEffect(() => {
    fetchKRAs();
  }, [currentPage, pageSize, searchQuery, designationFilter]);

  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [searchQuery, designationFilter]);

  if (loading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "80vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 12, mb: 10 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
        <Box>
          <Box sx={{ mb: 3 }}>
            <PageTitle>KRA Library</PageTitle>
          </Box>
          <Typography variant="body1" color="black">
            View and manage all KRAs
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => navigate("/pms/designations")}
          sx={{ height: "fit-content" }}
        >
          Create New KRA
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="body2" color="black">
                Total KRAs
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "bold", color: "primary.main" }}>
                {stats.total_kras}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="body2" color="black">
                Active KRAs
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "bold", color: "success.main" }}>
                {stats.active_kras}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="body2" color="black">
                Total Assignments
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "bold", color: "info.main" }}>
                {stats.total_assignments}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="body2" color="black">
                Drafts
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "bold", color: "warning.main" }}>
                {stats.draft_kras}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Role</InputLabel>
              <Select
                value={designationFilter}
                label="Designation"
                onChange={(e) => setDesignationFilter(e.target.value)}
              >
                <MenuItem value="all">All Roles</MenuItem>
                {filterOptions.designations.map((designation) => (
                  <MenuItem key={designation} value={designation}>
                    {designation}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* KRAs Table */}
      {kras.length === 0 ? (
        <Paper elevation={2} sx={{ p: 6, textAlign: "center" }}>
          <AssignmentIcon sx={{ fontSize: 80, color: "black", mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1, fontWeight: "600" }}>
            No KRAs Found
          </Typography>
          <Typography variant="body2" color="black" sx={{ mb: 3 }}>
            {kras.length === 0
              ? "No KRAs have been created yet."
              : "No KRAs match your search criteria."}
          </Typography>
          {kras.length === 0 && (
            <Button
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => navigate("/pms/designations")}
            >
              Create First KRA
            </Button>
          )}
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "grey.100" }}>
                <TableCell sx={{ fontWeight: "bold" }} align="center">
                  <TableSortLabel
                    active={sortField === "form_name"}
                    direction={sortField === "form_name" ? sortOrder : "asc"}
                    onClick={() => handleSort("form_name")}
                  >
                    KRA
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>
                  <TableSortLabel
                    active={sortField === "designation__designation_name"}
                    direction={sortField === "designation__designation_name" ? sortOrder : "asc"}
                    onClick={() => handleSort("designation__designation_name")}
                  >
                    Roles
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="center">
                  <TableSortLabel
                    active={sortField === "review_period"}
                    direction={sortField === "review_period" ? sortOrder : "asc"}
                    onClick={() => handleSort("review_period")}
                  >
                    Review Period
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", width: 90 }} align="center">Assignments</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="center">Status</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {kras.map((kra) => (
                <TableRow key={kra.id} hover>
                  <TableCell>
                    <Box>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: "600",
                          color: "primary.main",
                          cursor: "pointer",
                          "&:hover": {
                            textDecoration: "underline",
                            color: "primary.dark"
                          }
                        }}
                        onClick={() => handleViewKRA(kra)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleViewKRA(kra);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label={`View details for ${kra.form_name}`}
                      >
                        {kra.form_name}
                      </Typography>
                      <Typography variant="caption" color="black">
                        {kra.sections.length} KRA sections
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{kra.designation}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={getPeriodShortLabel(kra.review_period)} 
                      size="small" 
                      color="info" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ width: 90 }}>
                    <Chip
                      label={kra.total_assignments}
                      size="small"
                      color={kra.total_assignments > 0 ? "primary" : "default"}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {kra.is_draft ? (
                      <Chip label="Draft" size="small" color="warning" />
                    ) : (
                      <Chip label="Active" size="small" color="success" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                      <IconButton
                        size="small"
                        onClick={() => handleViewKRA(kra)}
                        title="View Details"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <Tooltip
                        title={
                          kra.total_assignments > 0
                            ? "This template has active assignments. Only the template name can be edited."
                            : "Edit KRA"
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleEditKRA(kra)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        size="small"
                        onClick={() => handleAssignKRA(kra)}
                        title="Assign to Employees"
                        disabled={kra.is_draft}
                      >
                        <AssignmentIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(kra)}
                        title={
                          kra.total_assignments > 0
                            ? `Cannot delete - ${kra.total_assignments} active assignment(s)`
                            : "Delete KRA"
                        }
                        disabled={kra.total_assignments > 0}
                        sx={{
                          color: kra.total_assignments > 0 ? "text.disabled" : "error.main",
                          "&:hover": {
                            backgroundColor: kra.total_assignments > 0 ? "transparent" : "error.lighter",
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination Controls */}
      {kras.length > 0 && (
        <StandardTablePagination
          component="div"
          count={totalCount}
          page={currentPage - 1}
          onPageChange={(_: unknown, newPage: number) => setCurrentPage(newPage + 1)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setPageSize(Number(e.target.value));
            setCurrentPage(1);
          }}
          sx={{ borderTop: "1px solid rgba(224, 224, 224, 1)" }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {kraToDelete && kraToDelete.total_assignments > 0 ? "Cannot Delete Template" : "Delete KRA Template"}
        </DialogTitle>
        <DialogContent>
          {kraToDelete && (
            <Box>
              {kraToDelete.total_assignments > 0 ? (
                <>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={{ fontWeight: "600", mb: 1 }}>
                      This template cannot be deleted!
                    </Typography>
                    <Typography variant="body2">
                      This template has {kraToDelete.total_assignments} active employee assignment(s). 
                      You must remove or complete all assignments before deleting this template.
                    </Typography>
                  </Alert>
                  <Box sx={{ p: 2, backgroundColor: "grey.100", borderRadius: 1, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: "600", mb: 0.5 }}>
                      {kraToDelete.form_name}
                    </Typography>
                    <Typography variant="body2" color="black">
                      Designation: {kraToDelete.designation}
                    </Typography>
                    <Typography variant="body2" color="black">
                      Sections: {kraToDelete.sections.length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "error.main", fontWeight: "600" }}>
                      Active Assignments: {kraToDelete.total_assignments}
                    </Typography>
                  </Box>
                  <Alert severity="info">
                    <Typography variant="body2">
                      <strong>Tip:</strong> You can view assignments in the "KRA Assignments" page and wait for 
                      employees to complete their reviews, or contact an administrator to manage assignments.
                    </Typography>
                  </Alert>
                </>
              ) : (
                <>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Are you sure you want to delete this KRA template?
                  </Typography>
                  <Box sx={{ p: 2, backgroundColor: "grey.100", borderRadius: 1, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: "600", mb: 0.5 }}>
                      {kraToDelete.form_name}
                    </Typography>
                    <Typography variant="body2" color="black">
                      Designation: {kraToDelete.designation}
                    </Typography>
                    <Typography variant="body2" color="black">
                      Sections: {kraToDelete.sections.length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "success.main", fontWeight: "600" }}>
                      Assignments: {kraToDelete.total_assignments} ✓
                    </Typography>
                  </Box>
                  <Alert severity="warning">
                    <Typography variant="body2" sx={{ fontWeight: "600" }}>
                      This action cannot be undone!
                    </Typography>
                  </Alert>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {kraToDelete && kraToDelete.total_assignments > 0 ? (
            <Button onClick={handleDeleteCancel} variant="contained">
              Close
            </Button>
          ) : (
            <>
              <Button onClick={handleDeleteCancel} disabled={deleting}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDeleteConfirm}
                disabled={deleting}
                startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
              >
                {deleting ? "Deleting..." : "Delete"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* View KRA Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {selectedKRA?.form_name}
            </Typography>
            <Typography variant="body2" color="black">
              {selectedKRA?.designation}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedKRA && (
            <Box>
              {/* KRA Sections */}
              <Typography variant="subtitle1" sx={{ fontWeight: "600", mb: 2 }}>
                KRA Sections
              </Typography>
              {selectedKRA.sections.map((section: KRASection, sectionIndex: number) => (
                <Accordion key={sectionIndex} elevation={0} sx={{ mb: 1 }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ backgroundColor: "grey.50" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                        pr: 2,
                      }}
                    >
                      <Typography sx={{ fontWeight: "600" }}>
                        {sectionIndex + 1}. {section.section}
                      </Typography>
                      <Typography sx={{ color: "primary.main", fontWeight: "600" }}>
                        {section.weightage}%
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ pl: 2 }}>
                      {section.questions.map((kpi: KPIQuestion, kpiIndex: number) => (
                        <Box
                          key={kpiIndex}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            py: 1.5,
                            borderBottom:
                              kpiIndex < section.questions.length - 1
                                ? "1px solid"
                                : "none",
                            borderColor: "grey.200",
                          }}
                        >
                          <Typography variant="body2" sx={{ flex: 1, fontWeight: "bold", fontSize: "15px" }}>
                            • {kpi.question}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "success.main",
                              fontWeight: "600",
                              ml: 2,
                              minWidth: "50px",
                              textAlign: "right",
                            }}
                          >
                            {kpi.weightage}%
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          {selectedKRA && !selectedKRA.is_draft && (
            <Button
              variant="contained"
              onClick={() => {
                handleAssignKRA(selectedKRA);
                setViewDialogOpen(false);
              }}
            >
              Assign to Employees
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AllKRATemplates;
